import React, { useCallback, useEffect, useRef } from 'react';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/dates';
import { t } from '../utils/translations';
import { printHtmlElement } from '../utils/printHtml';
import { sendInvoiceOnWhatsApp, shareInvoicePdfOnWhatsApp } from '../utils/whatsapp';
import './InvoicePrint.css';

const INVOICE_IFRAME_PRINT_CSS = `
@page { size: A5 portrait; margin: 8mm; }
@media print {
  @page { size: A5 portrait; margin: 8mm; }
  html, body {
    width: 100% !important;
    max-width: none !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }
  .invoice-print-sheet,
  .invoice-print-content {
    width: 100% !important;
    max-width: none !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }
  .invoice-table,
  .invoice-summary {
    width: 100% !important;
    max-width: none !important;
  }
  .invoice-summary {
    margin-left: 0 !important;
  }
}
`;

const InvoicePrint = ({ invoice, customer, onClose, autoPrint = false }) => {
  const sheetRef = useRef(null);

  const runPrint = useCallback(() => {
    printHtmlElement(sheetRef.current, {
      title: invoice?.invoice_number || 'Invoice',
      extraCss: INVOICE_IFRAME_PRINT_CSS,
      pageWidth: '148mm',
      pageHeight: '210mm'
    });
  }, [invoice]);

  useEffect(() => {
    if (!invoice) return undefined;
    document.body.classList.add('invoice-print-active');
    return () => document.body.classList.remove('invoice-print-active');
  }, [invoice]);

  useEffect(() => {
    if (!invoice || !autoPrint) return undefined;
    const timer = setTimeout(runPrint, 400);
    return () => clearTimeout(timer);
  }, [invoice, autoPrint, runPrint]);

  if (!invoice) return null;

  const handlePrint = () => {
    runPrint();
  };

  const handleWhatsApp = () => {
    sendInvoiceOnWhatsApp(invoice, customer);
  };

  const handleWhatsAppPdf = async () => {
    try {
      const result = await shareInvoicePdfOnWhatsApp(invoice, customer);
      if (result.mode === 'download+text') {
        alert('PDF downloaded. Attach it in WhatsApp (the message is ready).');
      }
    } catch (err) {
      console.error(err);
      alert('Could not prepare invoice PDF for WhatsApp');
    }
  };

  const customerName = customer?.name || invoice.customer_name || 'N/A';
  const issueDate = formatDate(invoice.issue_date);
  const dueDate = formatDate(invoice.due_date, '');
  const showItemDiscount = (invoice.items || []).some((item) => Number(item.discount) > 0);
  const isGstInvoice =
    Number(invoice.tax_amount) > 0 ||
    Number(invoice.cgst_amount) > 0 ||
    Number(invoice.sgst_amount) > 0;
  const clientGstNumber = (invoice.client_gst_number || '').trim();

  return (
    <div className="invoice-print-container">
      <div className="invoice-print-actions no-print">
        <span className="invoice-print-hint">Print size: A5 (148 × 210 mm)</span>
        <button type="button" onClick={handleWhatsAppPdf} className="btn btn-whatsapp" title="Share invoice PDF on WhatsApp (free)">
          WhatsApp PDF
        </button>
        <button type="button" onClick={handleWhatsApp} className="btn btn-whatsapp" title="Send invoice text on WhatsApp (free)">
          WhatsApp Text
        </button>
        <button type="button" onClick={handlePrint} className="btn btn-primary">
          {t('Print Invoice')}
        </button>
        <button type="button" onClick={onClose} className="btn btn-secondary">
          {t('Close')}
        </button>
      </div>

      <div className="invoice-print-sheet">
        <div className="invoice-print-content" ref={sheetRef}>
          <header className="invoice-header">
            <div className="invoice-company">
              <h1>{invoice.client_name || t('Inventory Management System')}</h1>
              {invoice.client_address ? <p>{invoice.client_address}</p> : null}
              <p className="invoice-company-contacts">
                {[invoice.client_phone && `${t('Phone')}: ${invoice.client_phone}`, invoice.client_email && `${t('Email')}: ${invoice.client_email}`]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {isGstInvoice && clientGstNumber ? (
                <p className="invoice-company-gstin">GSTIN: {clientGstNumber}</p>
              ) : null}
            </div>
            <div className="invoice-title-block">
              <div className="invoice-doc-label">{t('Invoice')}</div>
              <div className="invoice-number">{invoice.invoice_number}</div>
              <div className="invoice-meta-line">
                <span>{t('Date')}: {issueDate}</span>
                {dueDate ? <span>{t('Due')}: {dueDate}</span> : null}
              </div>
              <div className={`invoice-status status-${invoice.status}`}>{invoice.status}</div>
            </div>
          </header>

          <section className="invoice-billto">
            <h3>{t('Bill To')}</h3>
            <div className="customer-info">
              <p className="customer-name">{customerName}</p>
              {customer?.address ? <p>{customer.address}</p> : null}
              {(customer?.city || customer?.state || customer?.zip_code) ? (
                <p>
                  {[customer.city, customer.state, customer.zip_code].filter(Boolean).join(', ')}
                </p>
              ) : null}
              {customer?.phone ? <p>{t('Phone')}: {customer.phone}</p> : null}
              {customer?.tax_id ? <p>{t('Tax ID')}: {customer.tax_id}</p> : null}
            </div>
          </section>

          <section className="invoice-items">
            <table className="invoice-table">
              <thead>
                <tr>
                  <th className="col-sr">Sr</th>
                  <th className="col-desc">{t('Description')}</th>
                  <th className="col-qty">{t('Qty')}</th>
                  <th className="col-price">{t('Price')}</th>
                  {showItemDiscount ? <th className="col-disc">{t('Disc.')}</th> : null}
                  <th className="col-total">{t('Total')}</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items && invoice.items.length > 0 ? (
                  invoice.items.map((item, index) => {
                    const itemTotal = (item.quantity * (item.unit_price || 0)) - (item.discount || 0);
                    return (
                      <tr key={index}>
                        <td className="col-sr">{index + 1}</td>
                        <td className="col-desc">{item.description || 'N/A'}</td>
                        <td className="col-qty">{item.quantity || 0}</td>
                        <td className="col-price">{formatCurrency(item.unit_price || 0)}</td>
                        {showItemDiscount ? (
                          <td className="col-disc">
                            {Number(item.discount) > 0 ? formatCurrency(item.discount) : '—'}
                          </td>
                        ) : null}
                        <td className="col-total">{formatCurrency(itemTotal)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={showItemDiscount ? 6 : 5} className="invoice-empty">{t('No items')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          <section className="invoice-summary">
            <table className="invoice-summary-table">
              <tbody>
                <tr>
                  <td>{t('Subtotal')}</td>
                  <td className="summary-amount">{formatCurrency(invoice.subtotal || 0)}</td>
                </tr>
                {Number(invoice.tax_amount) > 0 && (() => {
                  const taxAmount = Number(invoice.tax_amount) || 0;
                  let cgstAmount = Number(invoice.cgst_amount) || 0;
                  let sgstAmount = Number(invoice.sgst_amount) || 0;
                  if (cgstAmount <= 0 && sgstAmount <= 0 && taxAmount > 0) {
                    cgstAmount = Math.round((taxAmount / 2) * 1000) / 1000;
                    sgstAmount = Math.round((taxAmount - cgstAmount) * 1000) / 1000;
                  }
                  const halfRate = Number(invoice.tax_rate) > 0
                    ? (Number(invoice.tax_rate) / 2).toFixed(2)
                    : '';
                  return (
                    <>
                      <tr>
                        <td>{halfRate ? `CGST (${halfRate}%)` : 'CGST'}</td>
                        <td className="summary-amount">{formatCurrency(cgstAmount)}</td>
                      </tr>
                      <tr>
                        <td>{halfRate ? `SGST (${halfRate}%)` : 'SGST'}</td>
                        <td className="summary-amount">{formatCurrency(sgstAmount)}</td>
                      </tr>
                    </>
                  );
                })()}
                {Number(invoice.discount) > 0 && (
                  <tr>
                    <td>{t('Discount')}</td>
                    <td className="summary-amount">-{formatCurrency(invoice.discount || 0)}</td>
                  </tr>
                )}
                <tr className="summary-total">
                  <td>{t('Total Amount')}</td>
                  <td className="summary-amount">{formatCurrency(invoice.total_amount || 0)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {invoice.notes ? (
            <section className="invoice-notes">
              <h4>{t('Notes')}</h4>
              <p>{invoice.notes}</p>
            </section>
          ) : null}

          <footer className="invoice-footer">
            <p>{t('Thank you for your business!')}</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrint;
